@if (generateExportStarted) {
  <pm-dialog
    dialogVariant="dialog"
    dialogTitle="RESULT.KPIS.EXPORT_OPTIONS.EXPORT_IS_GENERATING"
    [secondaryCtaButtonConfig]="{ text: 'UTILITY.CLOSE' }"
    [primaryCtaButtonConfig]="{ visible: false }"
  >
    <p class="text-content-body-text text-base">
      {{
        'RESULT.KPIS.EXPORT_OPTIONS.EXPORT_GENERATING_EXPLANATION' | translate
      }}
    </p>
  </pm-dialog>
} @else {
  <pm-dialog
    dialogTitle="RESULT.KPIS.EXPORT_OPTIONS.TITLE"
    size="lg"
    contentClass="gap-4"
    [secondaryCtaButtonConfig]="{ dataTestid: 'export-cancel-button' }"
    [primaryCtaButtonConfig]="primaryCtaButtonConfig()"
  >
    <div>
      <pm-h2 class="pb-4">
        {{ 'RESULT.KPIS.EXPORT_OPTIONS.HEADERS.CONTENT' | translate }}
      </pm-h2>
      <mat-radio-group
        [(ngModel)]="contentMode"
        (change)="onContentModeChange($event)"
        data-testid="export-content-mode-radio-group"
      >
        @for (option of contentOptions; track option) {
          <div class="grid grid-cols-3 gap-8">
            <div class="col-span-2 flex flex-col gap-1">
              <mat-radio-button
                class="radioButton"
                [value]="option.value"
                color="primary"
                [disabled]="
                  periodsSapFormat &&
                  option.value !== contentModeEnum.FORECAST_ONLY
                "
                [attr.data-testid]="`export-content-mode-${option.value}`"
              >
                {{ option.label | translate }}
              </mat-radio-button>
              <pm-description class="ml-[44px] -translate-y-2">{{
                option.description | translate
              }}</pm-description>
            </div>

            <pm-description class="col-span-1 m-[10px]">
              {{ option.dateFcn().minDate }} - {{ option.dateFcn().maxDate }}
            </pm-description>
          </div>
        }
      </mat-radio-group>
    </div>
    <mat-divider />
    <div>
      <pm-h2 class="pb-4">
        {{ 'RESULT.KPIS.EXPORT_OPTIONS.HEADERS.LEVEL_OF_DETAIL' | translate }}
      </pm-h2>
      <mat-radio-group
        [(ngModel)]="detailMode"
        data-testid="export-detail-mode-radio-group"
      >
        @for (option of detailOptions; track option) {
          <div class="flex flex-col gap-2">
            <mat-radio-button
              class="radioButton"
              [value]="option.value"
              color="primary"
              [disabled]="
                (plnmgSapFormat || periodsSapFormat) &&
                option.value === 'AGGREGATED'
              "
              [attr.data-testid]="`export-detail-mode-${option.value}`"
            >
              {{ option.label | translate }}
            </mat-radio-button>
            <pm-description class="ml-[44px] text-sm -translate-y-2">{{
              option.description | translate
            }}</pm-description>
          </div>
        }
      </mat-radio-group>

      <div class="ml-[34px] pt-3">
        <pm-form-field
          label="RESULT.KPIS.EXPORT_OPTIONS.LEVEL_OF_DETAIL.SELECT_GROUPS"
        >
          <pm-form-input-select
            labelKey="name"
            propertyKey="key"
            sortByKey="order"
            data-testid="export-groups-select"
            [data]="groups"
            [multiSelect]="true"
            [allowSelectAll]="true"
            [clearable]="true"
            [allowUnselect]="true"
            [disabled]="detailMode !== 'DETAILED'"
            [(ngModel)]="selectedGroupKeys"
          />
        </pm-form-field>
      </div>
    </div>

    @if (additionalData.length > 0) {
      <mat-divider />
      <div>
        <div>
          <pm-h2 class="pb-4">
            {{
              'RESULT.KPIS.EXPORT_OPTIONS.ADDITIONAL_DATASET.EXTRA_FORECASTS'
                | translate
            }}
          </pm-h2>

          <pm-form-field
            class="mt-2"
            label="RESULT.KPIS.EXPORT_OPTIONS.ADDITIONAL_DATASET.SELECT_EXTRA_FORECAST"
          >
            <pm-form-input-select
              [(ngModel)]="selectedAdditionalData"
              [data]="additionalData"
              [allowSelectAll]="true"
              [multiSelect]="true"
              [clearable]="true"
              [allowUnselect]="true"
            />
          </pm-form-field>
        </div>
      </div>
    }
    <mat-divider />
    <div>
      <pm-h2 class="pb-4">
        {{ 'RESULT.KPIS.EXPORT_OPTIONS.HEADERS.ADVANCED_SETTINGS' | translate }}
      </pm-h2>
      <div class="flex gap-4">
        <mat-checkbox
          [disabled]="plnmgSapFormat || periodsSapFormat"
          color="primary"
          [(ngModel)]="wideFormat"
        >
          {{
            'RESULT.KPIS.EXPORT_OPTIONS.ADVANCED_SETTINGS.WIDE_FORMAT'
              | translate
          }}
        </mat-checkbox>
        <mat-checkbox
          color="primary"
          [(ngModel)]="exportTarget"
          [disabled]="periodsSapFormat"
        >
          {{
            'RESULT.KPIS.EXPORT_OPTIONS.ADVANCED_SETTINGS.EXPORT_TARGET'
              | translate
                : {
                    target: 'Actuals',
                  }
          }}
        </mat-checkbox>
        @if (plnmgSapFormatFeatureFlag().enabled) {
          <mat-checkbox
            [disabled]="resolution() !== 'monthly'"
            color="primary"
            [(ngModel)]="plnmgSapFormat"
            (change)="onPlnmgSapFormatCheckboxChange($event)"
          >
            {{
              'RESULT.KPIS.EXPORT_OPTIONS.ADVANCED_SETTINGS.SAP_FORMAT'
                | translate
            }}
          </mat-checkbox>
        }
        @if (periodsSapFormatFeatureFlag().enabled) {
          <mat-checkbox
            [disabled]="resolution() !== 'monthly'"
            color="primary"
            [(ngModel)]="periodsSapFormat"
            (change)="onPeriodsSapFormatCheckboxChange($event)"
          >
            {{
              'RESULT.KPIS.EXPORT_OPTIONS.ADVANCED_SETTINGS.SAP_FORMAT'
                | translate
            }}
          </mat-checkbox>
        }
        <mat-form-field appearance="outline">
          <mat-label>{{
            'RESULT.KPIS.EXPORT_OPTIONS.ADVANCED_SETTINGS.DIGITS' | translate
          }}</mat-label>
          <input
            matInput
            type="number"
            [(ngModel)]="decimalDigits"
            min="0"
            max="30"
            step="1"
          />
        </mat-form-field>
      </div>
    </div>
    <mat-divider />
    <div>
      <pm-h2 class="pb-4">
        {{ 'RESULT.KPIS.EXPORT_OPTIONS.HEADERS.FILE_FORMAT' | translate }}
      </pm-h2>
      <mat-radio-group
        [(ngModel)]="fileFormatMode"
        data-testid="export-file-format-radio-group"
      >
        @for (option of fileFormatOptions; track option) {
          <div>
            @if (
              !(
                !periodsSapFormatFeatureFlag().enabled && option.value === 'ZIP'
              )
            ) {
              <mat-radio-button
                [value]="option.value"
                color="primary"
                [disabled]="
                  (periodsSapFormat && option.value !== 'ZIP') ||
                  (!periodsSapFormat && option.value === 'ZIP')
                "
                [attr.data-testid]="`export-detail-format-${option.value}`"
              >
                {{ option.label | translate }}
              </mat-radio-button>
            }
          </div>
        }
      </mat-radio-group>
    </div>
  </pm-dialog>
}
